import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormHoneypot from '@/components/feature/FormHoneypot';
import TurnstileField, {
  type TurnstileFieldHandle,
} from '@/components/feature/TurnstileField';
import { avatars } from '@/config/siteImages';
import { isTurnstileConfigured } from '@/config/turnstileEnv';
import { honeypotFilled } from '@/lib/formSpamGuard';
import { submitEmailLead } from '@/services/submitToKailac';

export default function LeadMagnet() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileFieldHandle>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (!email) return;
    const fd = new FormData(e.currentTarget);
    if (honeypotFilled(fd)) {
      setSubmitted(true);
      return;
    }
    setLoading(true);
    try {
      let captchaVerified = false;
      if (isTurnstileConfigured()) {
        const token = await turnstileRef.current?.ensureToken();
        if (!token) {
          setFormError(t('form_captcha_failed'));
          return;
        }
        captchaVerified = true;
      }
      try {
        await submitEmailLead(email, 'homepage_checklist', {
          recaptchaVerified: captchaVerified,
        });
      } catch (_err) {
        // Silent fail
      }
      turnstileRef.current?.reset();
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const checklistItems = [
    t('checklist_item_1') || 'Personal & business credit checklist',
    t('checklist_item_2') || 'Documentation readiness guide',
    t('checklist_item_3') || 'Common mistakes that reduce your chances',
    t('checklist_item_4') || 'Step-by-step pre-application tips',
    t('checklist_item_5') || 'What lenders actually look for',
  ];

  return (
    <section className="py-20 bg-[#F8F3FB] px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left: Visual */}
            <div className="relative bg-[#4F2D63] p-10 lg:p-14 flex flex-col justify-center">
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 mb-6">
                  <i className="ri-gift-line text-[#DBB6EE] text-sm"></i>
                  <span className="text-[#DBB6EE] text-xs font-semibold">
                    {t('lead_magnet_badge') || 'Free Resource — No Sign-Up Needed'}
                  </span>
                </div>

                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
                  {t('lead_magnet_title') || 'Get the Free Funding Readiness Checklist'}
                </h2>
                <p className="text-white/70 text-base leading-relaxed mb-8">
                  {t('lead_magnet_desc') || "Not sure if you're ready to apply? Our expert-built checklist shows you exactly where you stand."}
                </p>

                <ul className="space-y-3">
                  {checklistItems.map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                      <span className="text-sm text-white/85">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right: Form */}
            <div className="p-10 lg:p-14 flex flex-col justify-center">
              <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-4">
                {t('lead_magnet_not_ready') || "Not Ready to Apply Yet? That's OK."}
              </p>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                {t('lead_magnet_cta_title') || 'Enter Your Email to Get the Checklist Instantly'}
              </h3>
              <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                {t('lead_magnet_cta_desc') || "We'll send the checklist to your inbox. No spam, no sales calls — just the guide. Unsubscribe anytime."}
              </p>

              {submitted ? (
                <div className="flex flex-col items-center text-center gap-3 py-8">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#F8F3FB]">
                    <i className="ri-check-double-line text-[#4F2D63] text-3xl"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">
                    {t('lead_magnet_success_title') || "You're on the list!"}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {t('lead_magnet_success_desc') || 'Check your inbox for the Funding Readiness Checklist. When you\'re ready to apply, our team is here.'}
                  </p>
                </div>
              ) : (
                <>
                  <form
                    id="homepage-lead-magnet-form"
                    onSubmit={handleSubmit}
                    className="relative space-y-4"
                  >
                    <FormHoneypot />
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        {t('lead_magnet_email_label') || 'Your Email Address'}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder="you@yourbusiness.com"
                        className="w-full px-4 py-3 rounded-xl border border-[#E9D9F2] text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#4F2D63] transition-colors"
                      />
                    </div>
                    <TurnstileField ref={turnstileRef} />
                    {formError ? (
                      <div className="space-y-1">
                        <p className="text-sm text-red-600" role="alert">
                          {formError}
                        </p>
                        {isTurnstileConfigured() ? (
                          <p className="text-xs text-gray-500">{t('form_captcha_hint')}</p>
                        ) : null}
                      </div>
                    ) : null}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3.5 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {loading
                        ? (t('lead_magnet_btn_loading') || 'Sending...')
                        : (t('lead_magnet_btn') || 'Send Me the Free Checklist →')}
                    </button>
                  </form>

                  <div className="mt-5 flex items-center gap-4">
                    <div className="flex -space-x-1.5">
                      {[avatars.small.lm1, avatars.small.lm2, avatars.small.lm3].map((src, i) => (
                        <div key={i} className="w-7 h-7 rounded-full border-2 border-white overflow-hidden flex-shrink-0">
                          <img src={src} alt="" className="w-full h-full object-cover object-top" />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('lead_magnet_social') || 'Joined 1,200+ business owners who received this guide'}
                    </p>
                  </div>

                  <p className="text-xs text-gray-400 mt-4">
                    {t('lead_magnet_privacy') || 'Privacy protected. Unsubscribe anytime. We never sell your email.'}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
